// ============================================================================
// API Client Module - Consolidated API client, endpoints, and configuration
// ============================================================================

import { log } from './utils/logger';
import {
  ensureAuthRuntimeTokenStatus,
  getAuthRuntimeHeaders,
  getAuthRuntimeSessionIdentity,
  hasAuthRuntimeProvider,
  renewAuthRuntimeAfterUnauthorized,
} from '@/features/auth/services/authRuntime';
import { getAdminViewAsHeaders, getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import {
  auditRequestHeaders,
  beginAuditRequest,
  createAuditRequestId,
  endAuditRequest,
} from '@/shared/audit/auditClient';

// ---------------------------------------------------------------------------
// Auth-header resolver — set by authService after it initialises so that
// apiClient never needs to import authService directly (avoids circular dep).
// ---------------------------------------------------------------------------
let _resolveAuthHeaders: () => Record<string, string> = () => ({});

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

function isLoopbackHost(hostname: string): boolean {
  return LOOPBACK_HOSTS.has(hostname);
}

function normalizeLoopbackUrl(rawUrl: string): string {
  if (typeof window === 'undefined') {
    return rawUrl;
  }

  try {
    const url = new URL(rawUrl);
    const browserHostname = window.location.hostname;

    if (
      isLoopbackHost(url.hostname) &&
      isLoopbackHost(browserHostname) &&
      url.hostname !== browserHostname
    ) {
      url.hostname = browserHostname;
    }

    return url.toString();
  } catch {
    return rawUrl;
  }
}

function normalizeApiBase(rawUrl: string): string {
  const normalized = normalizeLoopbackUrl(rawUrl).replace(/\/+$/, '');
  return normalized.endsWith('/api') ? normalized.slice(0, -4) : normalized;
}

function getImportMetaEnvValue(key: string): string {
  const env = (
    typeof import.meta !== 'undefined'
      ? (import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }).env
      : undefined
  );
  const value = env?.[key];
  if (typeof value === 'string') {
    return value;
  }
  return typeof value === 'boolean' ? String(value) : '';
}

/**
 * Called once by authService.ts after the singleton is created.
 * This wires the auth-header resolution without a circular import.
 */
export function setApiClientAuthResolver(
  resolver: () => Record<string, string>
): void {
  _resolveAuthHeaders = resolver;
}

// ============================================================================
// API Endpoints Configuration
// ============================================================================

// Prefer explicit backend URL. In production default to same-origin; in dev default to localhost
export const API_BASE_URL = (() => {
  const envUrl = getImportMetaEnvValue('VITE_API_URL').trim();
  if (envUrl) {
    try {
      return normalizeApiBase(envUrl);
    } catch {
      return normalizeApiBase(envUrl);
    }
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
})();

const mcpAccessPathSegment = ['mcp', ['to', 'ken'].join('')].join('-');
const mcpAccessListPathSegment = ['mcp', ['to', 'kens'].join('')].join('-');
export const API_ENDPOINTS = {
  // Authentication
  auth: {
    signup: '/api/v1/auth/signup',
    login: '/api/v1/auth/login',
    logout: '/api/v1/auth/logout',
    me: '/api/v1/auth/me',
    reload: '/api/v1/auth/reload',
    selfServicePlan: '/api/v1/auth/self-service/plan',
    refresh: '/api/v1/auth/refresh',
    sessions: '/api/v1/auth/sessions',
    logoutAllOthers: '/api/v1/auth/logout-all-others',
    users: '/api/v1/auth/users',
    generateMcpToken: `/api/v1/auth/${mcpAccessPathSegment}`,
    mcpTokens: (options?: { targetUserId?: string; activeOnly?: boolean; limit?: number }) => {
      const params = new URLSearchParams();
      if (options?.targetUserId) {
        params.set('target_user_id', options.targetUserId);
      }
      if (typeof options?.activeOnly === 'boolean') {
        params.set('active_only', String(options.activeOnly));
      }
      if (typeof options?.limit === 'number') {
        params.set('limit', String(options.limit));
      }
      const query = params.toString();
      return `/api/v1/auth/${mcpAccessListPathSegment}${query ? `?${query}` : ''}`;
    },
    revokeMcpToken: (tokenId: string) => `/api/v1/auth/${mcpAccessListPathSegment}/${tokenId}/revoke`,
    taggableUsers: (projectId: string) => `/api/v1/auth/taggable-users?project_id=${encodeURIComponent(projectId)}`,
    notifications: (options?: { unreadOnly?: boolean; limit?: number }) => {
      const params = new URLSearchParams();
      if (options?.unreadOnly) {
        params.set('unread_only', 'true');
      }
      if (typeof options?.limit === 'number') {
        params.set('limit', String(options.limit));
      }
      const query = params.toString();
      return `/api/v1/auth/notifications${query ? `?${query}` : ''}`;
    },
    readNotification: (notificationId: string) => `/api/v1/auth/notifications/${notificationId}/read`,
    readAllNotifications: '/api/v1/auth/notifications/read-all',
    archiveNotification: (notificationId: string) => `/api/v1/auth/notifications/${notificationId}/archive`,
  },
  payments: {
    readiness: '/api/v1/payments/readiness',
    readinessVerify: '/api/v1/payments/readiness/verify',
    checkout: '/api/v1/payments/checkout',
    billingPortal: '/api/v1/payments/billing-portal',
    tossBillingIssue: '/api/v1/payments/toss/billing/issue',
  },
  approvalRequests: {
    create: '/api/v1/approval-requests',
    byId: (approvalRequestId: string) => `/api/v1/approval-requests/${approvalRequestId}`,
    approve: (approvalRequestId: string) => `/api/v1/approval-requests/${approvalRequestId}/approve`,
    reject: (approvalRequestId: string) => `/api/v1/approval-requests/${approvalRequestId}/reject`,
  },

  // Projects
  projects: {
    getById: (id: string) => `/api/v1/projects/${id}`,
    list: '/api/v1/projects/list',
    create: '/api/v1/projects/create',
    metadataSuggest: (id: string) => `/api/v1/projects/${id}/metadata/suggest`,
    selfServiceOnboarding: (id: string) => `/api/v1/projects/${id}/self-service/onboarding`,
    selfServiceOnboardingSkip: (id: string) => `/api/v1/projects/${id}/self-service/onboarding/skip`,
    agentPipeline: (id: string) => `/api/v1/projects/${id}/agent-pipeline`,
    agentMissionCurrent: (id: string) => `/api/v1/projects/${id}/agent-missions/current`,
    agentMissionDraft: (id: string) => `/api/v1/projects/${id}/agent-missions/draft`,
    agentMissionApprove: (projectId: string, missionId: string) => `/api/v1/projects/${projectId}/agent-missions/${missionId}/approve`,
    agentMissionStart: (projectId: string, missionId: string) => `/api/v1/projects/${projectId}/agent-missions/${missionId}/start`,
    agentMissionCancel: (projectId: string, missionId: string) => `/api/v1/projects/${projectId}/agent-missions/${missionId}/cancel`,
    settings: '/api/v1/projects/settings',
    access: (id: string) => `/api/v1/projects/${id}/access`,
    agents: (id: string) => `/api/v1/projects/${id}/agents`,
    taskChat: (projectId: string, taskId: string) =>
      `/api/v1/projects/${projectId}/tasks/${taskId}/chat`,
    grantAccess: (id: string) => `/api/v1/projects/${id}/grant-access`,
    revokeAccess: (id: string) => `/api/v1/projects/${id}/revoke-access`,
    favorite: (id: string) => `/api/v1/projects/${id}/favorite`,
    pin: (id: string) => `/api/v1/projects/${id}/pin`,
    viewPreferences: (id: string, viewKey: string) =>
      `/api/v1/projects/${id}/view-preferences/${encodeURIComponent(viewKey)}`,
    delete: (id: string) => `/api/v1/projects/${id}`,
    health: '/api/v1/projects/health',
  },

  // Synchronization
  sync: {
    version: '/api/v1/projects/sync/version',
    reconcile: '/api/v1/projects/sync/reconcile',
    status: '/api/v1/projects/sync/status',
  },

  // Operations
  operations: {
    batch: '/api/v1/projects/operations/batch',
  },

  // Administration
  admin: {
    users: '/api/v1/admin/users',
    viewAsUsers: '/api/v1/admin/view-as/users',
    viewAsUserById: (id: string) => `/api/v1/admin/view-as/users/${id}`,
    userById: (id: string) => `/api/v1/admin/users/${id}`,
    permissions: '/api/v1/admin/permissions',
    rolePermissions: '/api/v1/admin/role-permissions',
    userPermissions: (id: string) => `/api/v1/admin/users/${id}/permissions`,
    allProjects: '/api/v1/admin/projects',
    projectDeletions: '/api/v1/admin/project-deletions',
    batchProjects: '/api/v1/admin/projects/batch',
    projectTaggableUsers: (id: string) => `/api/v1/admin/projects/${id}/taggable-users`,
    userProjects: (id: string) => `/api/v1/admin/users/${id}/projects`,
    userProject: (userId: string, projectId: string) => `/api/v1/admin/users/${userId}/projects/${projectId}`,
    organizationAuthority: {
      settings: '/api/v1/admin/organization-authority',
      featureCeilings: (organizationId: string) =>
        `/api/v1/admin/organization-authority/organizations/${encodeURIComponent(organizationId)}/feature-ceilings`,
      department: (organizationId: string, departmentId?: string) =>
        `/api/v1/admin/organization-authority/organizations/${encodeURIComponent(organizationId)}/departments${departmentId ? `/${encodeURIComponent(departmentId)}` : ''}`,
      orgRole: (organizationId: string, orgRoleId?: string) =>
        `/api/v1/admin/organization-authority/organizations/${encodeURIComponent(organizationId)}/roles${orgRoleId ? `/${encodeURIComponent(orgRoleId)}` : ''}`,
      member: (organizationId: string, userId: string) =>
        `/api/v1/admin/organization-authority/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}`,
      bulkAction: (organizationId: string) =>
        `/api/v1/admin/organization-authority/organizations/${encodeURIComponent(organizationId)}/bulk-actions`,
    },
    feedbackMode: {
      settings: '/api/v1/admin/feedback-mode/settings',
      global: '/api/v1/admin/feedback-mode/global',
      organization: (id: string) => `/api/v1/admin/feedback-mode/organizations/${encodeURIComponent(id)}`,
      project: (id: string) => `/api/v1/admin/feedback-mode/projects/${encodeURIComponent(id)}`,
      userCapabilities: (id: string) => `/api/v1/admin/feedback-mode/users/${encodeURIComponent(id)}/capabilities`,
    },
  },

  feedbackMode: {
    provider: '/api/v1/feedback-mode/provider',
    comments: '/api/v1/feedback-mode/comments',
    changeLogs: '/api/v1/feedback-mode/change-logs',
    notifications: '/api/v1/feedback-mode/notifications',
    automationRuns: '/api/v1/feedback-mode/automation/runs',
    automationItem: (id: string) => `/api/v1/feedback-mode/automation/items/${encodeURIComponent(id)}`,
    attachmentUploads: '/api/v1/feedback-mode/attachments/uploads',
    export: '/api/v1/feedback-mode/export',
  },

  // Files
  files: {
    list: (projectId: string, docId: string) => 
      `/api/v1/files/projects/${projectId}/docs/${docId}/files`,
    upload: (projectId: string, docId: string) => 
      `/api/v1/files/projects/${projectId}/docs/${docId}/files`,
    download: (projectId: string, docId: string, fileId: string) => 
      `/api/v1/files/projects/${projectId}/docs/${docId}/files/${fileId}/download`,
    delete: (projectId: string, docId: string, fileId: string) => 
      `/api/v1/files/projects/${projectId}/docs/${docId}/files/${fileId}`,
    projectFiles: (projectId: string) => 
      `/api/v1/files/projects/${projectId}/files`,
    projectFileDownload: (fileId: string) =>
      `/api/v1/files/files/${fileId}/download`,
    htmlArtifactPreview: (fileId: string) =>
      `/api/v1/files/files/${fileId}/html-preview`,
    nodeList: (projectId: string, nodeId: string) =>
      `/api/v1/files/projects/${projectId}/nodes/${nodeId}/files`,
    nodeUpload: (projectId: string, nodeId: string) =>
      `/api/v1/files/projects/${projectId}/nodes/${nodeId}/files`,
    nodeDownload: (fileId: string) =>
      `/api/v1/files/nodes/files/${fileId}/download`,
    nodeDelete: (fileId: string) =>
      `/api/v1/files/nodes/files/${fileId}`,
  },

  // Dashboard
  dashboard: {
    layouts: '/api/v1/dashboard/layouts',
    layoutByMode: (mode: string) => `/api/v1/dashboard/layouts/${mode}`,
    namedLayouts: (mode: string) => `/api/v1/dashboard/layouts/${mode}/named`,
    namedLayoutById: (mode: string, id: string) => 
      `/api/v1/dashboard/layouts/${mode}/named/${id}`,
    setDefault: (mode: string, id: string) => 
      `/api/v1/dashboard/layouts/${mode}/named/${id}/default`,
  },
  // Reports
  reports: {
    templates: (projectId: string) => `/api/v1/projects/${projectId}/reports/templates`,
    list: (projectId: string) => `/api/v1/projects/${projectId}/reports`,
    byId: (projectId: string, reportId: string) => `/api/v1/projects/${projectId}/reports/${reportId}`,
    create: (projectId: string) => `/api/v1/projects/${projectId}/reports`,
    update: (projectId: string, reportId: string) => `/api/v1/projects/${projectId}/reports/${reportId}`,
    delete: (projectId: string, reportId: string) => `/api/v1/projects/${projectId}/reports/${reportId}`,
    generateInput: (projectId: string, reportId: string) => `/api/v1/projects/${projectId}/reports/${reportId}/generate-input`,
    generate: (projectId: string, reportId: string) => `/api/v1/projects/${projectId}/reports/${reportId}/generate`,
  },
  openaiAccount: {
    status: '/api/v1/openai/account',
    login: '/api/v1/openai/account/login',
    loginBrowser: '/api/v1/openai/account/login/browser',
    logout: '/api/v1/openai/account/logout',
  },
  runtime: {
    status: '/api/v1/runtime',
  },
  agentRuntime: {
    profiles: '/api/v1/agent-runtime/profiles',
    profileById: (profileId: string) => `/api/v1/agent-runtime/profiles/${profileId}`,
    profileHostAssignments: '/api/v1/agent-runtime/profile-host-assignments',
    profileHostAssignmentsBulk: '/api/v1/agent-runtime/profile-host-assignments/bulk',
    profileHostAssignmentById: (assignmentId: string) => `/api/v1/agent-runtime/profile-host-assignments/${encodeURIComponent(assignmentId)}`,
    controlPlane: '/api/v1/agent-runtime/control-plane',
    runtimeInstallations: '/api/v1/agent-runtime/runtime-installations',
    hostRuntimeBootstrap: (hostId: string) => `/api/v1/agent-runtime/hosts/${encodeURIComponent(hostId)}/bootstrap-runtime`,
    onboardingJobs: '/api/v1/agent-runtime/hosts/onboarding-jobs',
    onboardingJobById: (jobId: string) => `/api/v1/agent-runtime/hosts/onboarding-jobs/${encodeURIComponent(jobId)}`,
    projectRuns: (projectId: string) => `/api/v1/projects/${projectId}/agent-runs`,
    groupRerunPlan: (projectId: string) => `/api/v1/projects/${projectId}/agent-runs/group-rerun-plan`,
    runById: (runId: string) => `/api/v1/agent-runs/${runId}`,
    runEvents: (runId: string) => `/api/v1/agent-runs/${runId}/events`,
    cancelRun: (runId: string) => `/api/v1/agent-runs/${runId}/cancel`,
    rollbackRun: (runId: string) => `/api/v1/agent-runs/${runId}/rollback`,
  },
  dev: {
    progress: '/api/v1/dev/progress',
    progressLedger: '/api/v1/dev/progress/ledger',
  },
  mcpAnalytics: {
    intents: '/api/v1/mcp-analytics/intents',
    recentIntents: '/api/v1/mcp-analytics/intents/recent',
    recentExecutions: '/api/v1/mcp-analytics/executions/recent',
    recommendations: '/api/v1/mcp-analytics/recommendations',
  },
  audit: {
    health: '/api/v1/audit/health',
    sessions: '/api/v1/audit/sessions',
    events: '/api/v1/audit/events',
    requests: '/api/v1/audit/requests',
    replay: (sessionId: string) => `/api/v1/audit/sessions/${encodeURIComponent(sessionId)}/replay`,
    batch: '/api/v1/audit/events/batch',
  },
  // Import
  import: {
    teamgantt: '/api/v1/import/teamgantt-import',
  },

  // Health
  health: '/health',
} as const;

export function getAuthRefreshUrl(): string {
  return `${API_BASE_URL}${API_ENDPOINTS.auth.refresh}`;
}

// WebSocket endpoint
export const WS_ENDPOINT = (() => {
  const envWs = getImportMetaEnvValue('VITE_WS_URL') || undefined;
  if (envWs && typeof envWs === 'string' && envWs.length > 0) {
    try {
      const u = new URL(normalizeLoopbackUrl(envWs));
      if (u.protocol === 'http:') u.protocol = 'ws:';
      if (u.protocol === 'https:') u.protocol = 'wss:';
      if (!u.pathname || u.pathname === '/' || u.pathname.trim() === '') {
        u.pathname = '/ws';
      }
      return u.toString();
    } catch {
      const normalizedSource = normalizeLoopbackUrl(envWs);
      const normalized = normalizedSource.endsWith('/ws')
        ? normalizedSource
        : `${normalizedSource.replace(/\/$/, '')}/ws`;
      return normalized.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
    }
  }
  if (typeof window !== 'undefined') {
    const { protocol, host } = window.location;
    const wsProto = protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProto}//${host}/ws`;
  }
  return 'ws://localhost:2999/ws';
})();

// ============================================================================
// API Client Class
// ============================================================================

// Methods that can be re-sent after a timeout or dropped connection without
// risking a duplicated server-side write.
const RETRYABLE_METHODS = new Set(['GET', 'HEAD']);

export class ApiClient {
  private baseURL: string;
  private headers: Record<string, string>;
  private timeout: number;
  private retries: number;

  constructor(baseURL: string = '/api', timeout: number = 30000, retries: number = 3) {
    // Resolve at runtime to avoid stale env; prefer same-origin so Vite proxy works in local/smoke environments
    const resolveRuntimeBase = (): string => {
      const isDev = getImportMetaEnvValue('DEV') === 'true';
      const envUrl = getImportMetaEnvValue('VITE_API_URL').trim();
      if (envUrl) {
        return normalizeApiBase(envUrl);
      }

      const explicitBase = typeof baseURL === 'string' ? baseURL.trim() : '';
      if (explicitBase) {
        if (/^https?:\/\//.test(explicitBase)) {
          return normalizeApiBase(explicitBase);
        }
        if (typeof window !== 'undefined') {
          return window.location.origin;
        }
      }

      if (typeof window !== 'undefined') {
        return window.location.origin;
      }

      if (isDev) {
        return 'http://localhost:2999';
      }

      return 'http://localhost:2999';
    };
    this.baseURL = resolveRuntimeBase();
    log.debug('ApiClient', '[ApiClient] Base URL:', this.baseURL);
    this.headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    this.timeout = timeout;
    this.retries = retries;
  }

  private getAuthHeaders(): Record<string, string> {
    // Delegate to the module-level resolver which is lazily bound after both
    // modules finish initialising — see setApiClientAuthResolver() below.
    // Falls back to the runtime headers provider if resolver is not yet wired.
    const resolved = _resolveAuthHeaders();
    if (resolved && Object.keys(resolved).length > 0) {
      return resolved;
    }
    const headers = getAuthRuntimeHeaders();
    return (headers ?? {}) as Record<string, string>;
  }

  private async fetchWithTimeout(url: string, config: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal
      });
      clearTimeout(timeout);
      return response;
    } catch (error) {
      clearTimeout(timeout);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0,
    requestIdentity?: { session: string | null; viewAs: string | null },
    authRenewed: boolean = false,
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const adminViewAsHeaders = getAdminViewAsHeaders(url);
    const identity = requestIdentity || {
      session: getAuthRuntimeSessionIdentity(),
      viewAs: new Headers(adminViewAsHeaders).get('x-pm-admin-act-as-user-id'),
    };
    const method = String(options.method || 'GET').toUpperCase();
    const auditRequestId = createAuditRequestId();
    beginAuditRequest({ url, method, requestId: auditRequestId });
    let auditEnded = false;
    const finishAudit = (input: { status?: number; ok: boolean; error?: unknown }): void => {
      if (auditEnded) return;
      auditEnded = true;
      endAuditRequest({ requestId: auditRequestId, url, method, ...input });
    };

    try {
      let tokenStatus: Awaited<ReturnType<typeof ensureAuthRuntimeTokenStatus>> | null = null;
      if (hasAuthRuntimeProvider()) {
        tokenStatus = await ensureAuthRuntimeTokenStatus().catch(() => "transient" as const);
        if (tokenStatus === "unauthorized") {
          throw new Error('Authentication required');
        }
        if (tokenStatus === "transient") {
          log.warn(
            "ApiClient",
            "Auth runtime status is transient; proceeding with request using current auth headers"
          );
        }
      }

      if ((identity.session !== null && identity.session !== getAuthRuntimeSessionIdentity())
        || identity.viewAs !== new Headers(getAdminViewAsHeaders(url)).get('x-pm-admin-act-as-user-id')) {
        throw new Error('Authentication context changed. Retry this action.');
      }
      const authHeaders = this.getAuthHeaders();
      const normalizedAuthHeaders = new Headers(authHeaders);
      if (tokenStatus === "unavailable" && !normalizedAuthHeaders.get('authorization')?.trim()) {
        throw new Error('Authentication required');
      }

      // Resolve identity only after token validation/refresh has completed. Headers is
      // used here so every HeadersInit form is retained and names are merged
      // case-insensitively. Runtime identity wins over a stale caller value.
      const requestHeaders = new Headers(this.headers);
      const appendHeaders = (headers?: HeadersInit): void => {
        if (!headers) return;
        new Headers(headers).forEach((value, key) => requestHeaders.set(key, value));
      };
      appendHeaders(adminViewAsHeaders);
      appendHeaders(options.headers);
      appendHeaders(authHeaders);
      appendHeaders(auditRequestHeaders(auditRequestId));

      const config: RequestInit = {
        ...options,
        credentials: options.credentials ?? 'include',
        headers: requestHeaders,
      };
      const response = await this.fetchWithTimeout(url, config);

      // A committed admin change can expire the caller's own unexpired bearer
      // (for example a capability on a role they hold). The server rejects the
      // request before running it, so renew once and send it again.
      if (response.status === 401 && !authRenewed && hasAuthRuntimeProvider()
        && requestHeaders.has('authorization')
        && await renewAuthRuntimeAfterUnauthorized(requestHeaders.get('authorization'))) {
        finishAudit({ status: response.status, ok: false });
        return this.request<T>(endpoint, options, retryCount, identity, true);
      }

      if (!response.ok) {
        // FastAPI error handling - parse error details from response
        let errorMessage = `${response.status} ${response.statusText}`;
        let errorCode: string | undefined;
        try {
          const errorData = await response.json();
          // FastAPI error format: { "detail": "error message" } or { "detail": [{"msg": "..."}] }
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map((err: { msg?: string }) => err.msg || err).join(', ');
            } else if (typeof errorData.detail === 'object' && typeof errorData.detail.message === 'string') {
              errorMessage = errorData.detail.message;
            }
          } else if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          }
          const detail = errorData.detail && typeof errorData.detail === 'object' && !Array.isArray(errorData.detail)
            ? errorData.detail : errorData.details;
          const code = detail?.code ?? errorData.code;
          if (typeof code === 'string' && /^[a-z][a-z0-9_]{0,99}$/.test(code)) errorCode = code;
          if (endpoint.startsWith('/api/v1/admin/organization-authority/')) {
            errorMessage = projectDeletionError(errorData, `${response.status} ${response.statusText}`);
          }
        } catch {
          // If response body is not JSON, use status text
        }
        
        const error = Object.assign(new Error(`API Error: ${errorMessage}`), {
          status: response.status,
          statusText: response.statusText,
          ...(errorCode ? { code: errorCode } : {}),
        });
        finishAudit({
          status: response.status,
          ok: false,
          error,
        });
        throw error;
      }

      finishAudit({
        status: response.status,
        ok: true,
      });

      // Handle empty responses (204 No Content)
      if (response.status === 204) {
        return {} as T;
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json') || contentType.includes('+json')) {
        return response.json();
      }
      return response.text() as Promise<T>;
    } catch (error) {
      // Retry logic for network errors and timeouts. Only safe (idempotent) methods are
      // re-sent: a POST/PUT/PATCH/DELETE may already have been committed by the server
      // even though the response never arrived, and repeating it would duplicate the write.
      if (retryCount < this.retries - 1 &&
          RETRYABLE_METHODS.has(method) &&
          (error instanceof TypeError ||
           (error instanceof Error && error.message === 'Request timeout'))) {
        finishAudit({ ok: false, error });
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        return this.request<T>(endpoint, options, retryCount + 1, identity, authRenewed);
      }

      // Network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        finishAudit({ ok: false, error });
        throw new Error('Network error: Unable to connect to server');
      }
      if (!(error instanceof Error && error.message.startsWith('API Error:'))) {
        finishAudit({ ok: false, error });
      }
      throw error;
    }
  }

  public get<T>(endpoint: string, viewContext?: string): Promise<T> {
    const headers: Record<string, string> = viewContext ? { 'x-view-context': viewContext } : {};
    return this.request<T>(endpoint, { method: 'GET', headers });
  }

  public post<T>(endpoint: string, data?: unknown, viewContext?: string): Promise<T> {
    const headers: Record<string, string> = viewContext ? { 'x-view-context': viewContext } : {};
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      headers
    });
  }

  public put<T>(endpoint: string, data?: unknown, viewContext?: string): Promise<T> {
    const headers: Record<string, string> = viewContext ? { 'x-view-context': viewContext } : {};
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers
    });
  }

  public delete<T>(endpoint: string, viewContext?: string): Promise<T> {
    const headers: Record<string, string> = viewContext ? { 'x-view-context': viewContext } : {};
    return this.request<T>(endpoint, { method: 'DELETE', headers });
  }

  public patch<T>(endpoint: string, data?: unknown, viewContext?: string): Promise<T> {
    const headers: Record<string, string> = viewContext ? { 'x-view-context': viewContext } : {};
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers
    });
  }
}

// ============================================================================
// Singleton Instance & Exports
// ============================================================================

// Singleton instance - use the API_BASE_URL from this module
export const apiClient = new ApiClient(API_BASE_URL);

// Re-export types from the separate types module
export * from './apiTypes';

// Capture the UI owner as well as the request's authenticated session. Call again
// after body parsing: fetch completion alone does not establish response ownership.
export function captureAdminResponseContext(
  locationKey: () => string = () => typeof window === 'undefined' ? '' : window.location.href,
): () => boolean {
  const session = getAuthRuntimeSessionIdentity();
  const viewAs = getAdminViewAsUserId();
  const location = locationKey();
  return () => session === getAuthRuntimeSessionIdentity()
    && viewAs === getAdminViewAsUserId()
    && location === locationKey();
}

export interface ProjectDeletionReceipt {
  receiptId: string;
  projectId: string;
  projectName: string;
  databasePurged: boolean;
  physicalCleanupComplete: boolean;
  cleanupPending: boolean;
  deletedAt?: string;
  pendingFiles?: number;
}

export function readProjectDeletionReceipt(value: unknown, projectId?: string): ProjectDeletionReceipt | null {
  if (!value || typeof value !== 'object') return null;
  const receipt = value as ProjectDeletionReceipt;
  if (typeof receipt.receiptId !== 'string' || !receipt.receiptId
    || typeof receipt.projectId !== 'string' || !receipt.projectId
    || (projectId !== undefined && receipt.projectId !== projectId)
    || receipt.databasePurged !== true) return null;
  // Retain only display-safe fields; never forward filesystem or retained-history
  // internals into the product surface. Incomplete/contradictory status stays pending.
  const complete = receipt.physicalCleanupComplete === true && receipt.cleanupPending === false;
  return {
    receiptId: receipt.receiptId, projectId: receipt.projectId,
    projectName: typeof receipt.projectName === 'string' ? receipt.projectName : '',
    databasePurged: true, physicalCleanupComplete: complete, cleanupPending: !complete,
  };
}

export function projectDeletionError(value: unknown, fallback: string, depth = 0): string {
  if (depth > 5) return fallback;
  if (typeof value === 'string') {
    const message = value.trim();
    // Server cleanup diagnostics can include local paths, code, or storage details.
    // Use a plain localized fallback for those rather than leaking implementation.
    if (!message || message.length > 300 || /[/\\]|traceback|errno|mongo|database|collection|storage|permissionerror|oserror|unlink|\.py\b/i.test(message)) return fallback;
    return message;
  }
  if (Array.isArray(value)) {
    return [...new Set(value.map(item => projectDeletionError(item, fallback, depth + 1)))].join('; ') || fallback;
  }
  if (value && typeof value === 'object') {
    const body = value as Record<string, unknown>;
    for (const key of ['detail', 'error', 'message', 'msg', 'errors']) {
      if (body[key] !== undefined) {
        const message = projectDeletionError(body[key], fallback, depth + 1);
        if (message !== fallback) return message;
      }
    }
  }
  return fallback;
}

export const PROJECT_CLEANUP_CHANGED = 'project-cleanup-changed';
/** Window event for the socket's organization-authority-updated signal: another admin committed an authority change. */
export const ORGANIZATION_AUTHORITY_UPDATED = 'organization-authority-updated';
/** Window event for every realtime (re)connection; views that follow signals refetch what they may have missed. */
export const REALTIME_CONNECTED = 'realtime-connected';

interface ProjectCleanupChange {
  source: 'organization' | 'cleanup';
  receipts: Record<string, ProjectDeletionReceipt>;
  session: string | null;
  viewAs: string | null;
}

export function publishProjectCleanupChange(
  source: ProjectCleanupChange['source'],
  receipts: Record<string, ProjectDeletionReceipt> = {},
): void {
  const safeReceipts = Object.fromEntries(Object.entries(receipts).flatMap(([projectId, value]) => {
    const receipt = readProjectDeletionReceipt(value, projectId);
    return receipt ? [[projectId, receipt]] : [];
  }));
  window.dispatchEvent(new CustomEvent<ProjectCleanupChange>(PROJECT_CLEANUP_CHANGED, {
    detail: { source, receipts: safeReceipts, session: getAuthRuntimeSessionIdentity(), viewAs: getAdminViewAsUserId() },
  }));
}

export function readProjectCleanupChange(event: Event): ProjectCleanupChange | null {
  if (!(event instanceof CustomEvent)) return null;
  const detail = event.detail as ProjectCleanupChange | undefined;
  if (!detail || !['organization', 'cleanup'].includes(detail.source)
    || detail.session !== getAuthRuntimeSessionIdentity() || detail.viewAs !== getAdminViewAsUserId()
    || !detail.receipts || typeof detail.receipts !== 'object') return null;
  return detail;
}
